import { useMemo, createContext, useContext, useEffect, useRef, useState } from 'react';
import type {
  GetGroupedWorklistItemsQuery,
  GetGroupedWorklistItemsQueryVariables,
} from 'generated/graphql';
import { useQuery } from '@apollo/client';
import { GET_GROUPED_WORKLIST_ITEMS } from 'modules/Apollo/queries';
import { Editor, Transforms, Element } from 'slate';
import type { Text } from 'slate';
import { PLACEHOLDER_PLUGIN_ID } from '../domains/reporter/RichTextEditor/plugins/placeholder/types';
import { PLACEHOLDER_FIELDS_TO_APPLY_TO_LINK_CASES } from 'domains/reporter/RichTextEditor/plugins/placeholder/constants';
import type { PlaceholderFieldToApplyToLinkCases } from 'domains/reporter/RichTextEditor/plugins/placeholder/constants';
import {
  resolvePlaceholderID,
  getUniqueWorklistItems,
  getDuplicateWorklistItem,
  handleInsertPlaceholders,
  extractCustomMergeFieldKeys,
} from '../domains/reporter/RichTextEditor/plugins/placeholder/utils';
import {
  NameSpaces,
  useCrossTabEventsListener,
  CrossWindowDataRefetchType,
} from '../modules/EventsManager/eventsManager';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useSlate } from 'domains/reporter/RichTextEditor/core';

export const sortByStudyDescAndAccession = (
  a: GroupedWorklistItem | PartialWorklistItem,
  b: GroupedWorklistItem | PartialWorklistItem
): number => {
  const aStudyDesc = a.studyDescription ?? '';
  const bStudyDesc = b.studyDescription ?? '';
  const aAcc = a.accessionNumber ?? '';
  const bAcc = b.accessionNumber ?? '';
  return aStudyDesc.localeCompare(bStudyDesc) || aAcc.localeCompare(bAcc);
};

export type PartialWorklistItem = {
  accessionNumber: Nullable<string>;
  claimedBy: Nullable<string>;
  smid: string;
  studyDate: Nullable<Date>;
  studyDescription: Nullable<string>;
  groupId: Nullable<string>;
};
export type GroupedWorklistItem = GetGroupedWorklistItemsQuery['groupedWorklistItems'][0];
export type GroupedWorklistItems = ReadonlyArray<GroupedWorklistItem>;

/** Stable identity, so a case with no held list does not hand the memo below a new array each render. */
const EMPTY_GROUP: GroupedWorklistItems = [];

const getPlaceholdersToProcess = (
  items: GroupedWorklistItems
): Array<[string, PlaceholderFieldToApplyToLinkCases | string]> => {
  const canonicalPlaceholders = Object.entries(PLACEHOLDER_FIELDS_TO_APPLY_TO_LINK_CASES) as Array<
    [string, PlaceholderFieldToApplyToLinkCases]
  >;

  const customMergeFieldPlaceholders = [...extractCustomMergeFieldKeys(items)].map(
    (key) => [key, key] as [string, string]
  );

  return [...canonicalPlaceholders, ...customMergeFieldPlaceholders];
};

export const handleAddWorklistItem = (
  addedWorklistItem: GroupedWorklistItem,
  currentWorklistItems: GroupedWorklistItems,
  editor: Editor
) => {
  Editor.withoutNormalizing(editor, () => {
    const placeholders = getPlaceholdersToProcess(currentWorklistItems);

    // For each placeholder, find the unique worklist items for that placeholder field.
    placeholders.forEach(([placeholderID, placeholderField]) => {
      const uniqueWorklistItems = getUniqueWorklistItems(currentWorklistItems, placeholderField);

      // If the added item is one of the unique items for this placeholder, then we need to insert a new placeholder node. This node should be inserted after the placeholder node for the unique item that comes before the added item.
      if (uniqueWorklistItems.some((item) => item.smid === addedWorklistItem.smid)) {
        const addedWorklistIdx = uniqueWorklistItems.findIndex(
          (item) => item.smid === addedWorklistItem.smid
        );

        // We want to insert the placeholder after the placeholder for the unique item that comes before the added item.
        // But the user may have deleted that placeholder previously. So we look backwards iteratively until we find a placeholder that exists.
        let uniqueInFrontOfMeIdx = addedWorklistIdx - 1;
        let placeholdersForUniqueInFrontOfMe = null;
        while (uniqueInFrontOfMeIdx >= 0) {
          const uniqueInFrontOfMe = uniqueWorklistItems[uniqueInFrontOfMeIdx];
          placeholdersForUniqueInFrontOfMe = Editor.nodes(editor, {
            at: [],
            match: (n) =>
              Element.isElement(n) &&
              n.type === PLACEHOLDER_PLUGIN_ID &&
              n.placeholderID === placeholderID &&
              n.workListItemSmid === uniqueInFrontOfMe.smid,
          });

          if (placeholdersForUniqueInFrontOfMe != null) {
            for (const [node, path] of placeholdersForUniqueInFrontOfMe) {
              // handleInsertPlaceholders will removeNodes at the provided path, so we include the node for the uniqueInFrontOfMe so that it will be re-inserted.
              const nodesToInsert = [
                node,
                { text: ', ' },
                {
                  type: PLACEHOLDER_PLUGIN_ID,
                  placeholderID,
                  workListItemSmid: addedWorklistItem.smid,
                  children: [{ text: resolvePlaceholderID(placeholderID, addedWorklistItem) }],
                },
              ];

              // handleInsertPlaceholders will move the selection. We want to preserve the selection, so we store it before and restore it after.
              const selectionBeforeInsert = editor.selection;
              handleInsertPlaceholders(editor, path, nodesToInsert);
              if (selectionBeforeInsert != null) {
                Transforms.select(editor, selectionBeforeInsert);
              }
              return;
            }
          }

          uniqueInFrontOfMeIdx--;
        }
      }
    });
  });
};

// Important to note that, at this point, currentWorklistItems *does not include* the removedWorklistItem.
export const handleRemoveWorklistItem = (
  removedWorklistItem: GroupedWorklistItem,
  currentWorklistItems: GroupedWorklistItems,
  editor: Editor
) => {
  Editor.withoutNormalizing(editor, () => {
    const placeholders = getPlaceholdersToProcess(currentWorklistItems);

    // For each placeholder, find the worklist items which have a duplicate value to the removed worklist item for that placeholder field.
    placeholders.forEach(([placeholderID, placeholderField]) => {
      const duplicateWorklistItem = getDuplicateWorklistItem(
        removedWorklistItem,
        currentWorklistItems,
        placeholderField
      );

      // For each placeholder associated with the removed worklist item, we need to either:
      // 1: Replace it's workListItemSmid, if there is a duplicate value among the remaining worklist items.
      // 2: Remove it, if there is no duplicate value among the remaining worklist items. Also, remove the preceding ", " if there is one.
      const placeholdersOfRemoved = Editor.nodes(editor, {
        at: [],
        match: (n) =>
          Element.isElement(n) &&
          n.type === PLACEHOLDER_PLUGIN_ID &&
          n.placeholderID === placeholderID &&
          n.workListItemSmid === removedWorklistItem.smid,
      });
      for (const [, path] of Array.from(placeholdersOfRemoved)) {
        if (duplicateWorklistItem != null) {
          Transforms.setNodes(
            editor,
            { workListItemSmid: duplicateWorklistItem.smid },
            { at: path }
          );
        } else {
          Transforms.removeNodes(editor, { at: path });
          Transforms.insertNodes(editor, { children: [{ text: '' }] }, { at: path });

          const precedingNode = Editor.previous(editor, { at: path });
          if (precedingNode != null && (precedingNode[0] as Text).text === ', ') {
            Transforms.removeNodes(editor, { at: precedingNode[1] });
            Transforms.insertNodes(editor, { text: '' }, { at: precedingNode[1] });
          }
        }
      }
    });
  });
};

const CurrentWorklistItems = createContext<{
  loading: boolean;
  currentWorklistItems: GroupedWorklistItems;
}>({
  loading: true,
  currentWorklistItems: [],
});

export const CurrentWorklistItemsProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const [previousWorklistItems, setPreviousWorklistItems] = useState<GroupedWorklistItems>([]);
  /** Case the reconciliation baseline below was taken from. See the effect that reads it. */
  const reconciledCaseSmidRef = useRef<Nullable<string>>(null);
  const editor = useSlate();
  const { currentCase: worklistItem, refreshCase, loadingCase } = useCurrentCaseContext();

  const {
    refetch,
    loading: loadingGroup,
    data,
  } = useQuery<GetGroupedWorklistItemsQuery, GetGroupedWorklistItemsQueryVariables>(
    GET_GROUPED_WORKLIST_ITEMS,
    {
      fetchPolicy: 'cache-and-network',
      skip: worklistItem == null,
      variables: {
        groupId: worklistItem?.groupId,
      },
      context: {
        telemetry: { ...APOLLO_QUERY_CONTEXT.telemetry, fetchPolicy: 'cache-and-network' },
      },
    }
  );

  // A client with `errorPolicy: 'all'` hands back `data: null` (not `undefined`) when the query errors, so a
  // destructuring default is not enough to keep this provider from taking the whole tree down with it.
  //
  // Falling back to the last good list rather than to `[]` matters beyond the crash: the reconciliation effect below
  // reads a shrunken list as "these cases were unlinked" and rewrites the report's placeholders to match, so a blank
  // list from a transient failure would edit an in-progress report on the reader's behalf. Holding the previous list
  // leaves the report alone until the query recovers and reports an unlink that actually happened.
  // The fallback is scoped to the query it answered for -- the case and the group id the query ran on. One list held
  // for the life of the provider would answer for whatever the reader opened next, so a failed query would show the
  // previous linked cases and reconcile the current report against them. The group id is half of that key because it
  // is the query's own variable: a case whose group changes or is dissolved is asking a different question, and the
  // previous answer describes cases that are no longer linked.
  const queryKey =
    worklistItem == null ? null : `${worklistItem.smid}:${worklistItem.groupId ?? ''}`;
  // Held in a ref and written during render rather than from an effect. The write is idempotent -- it only ever
  // records the answer this render already has -- and a ref keeps it out of the render cycle. As state it looped:
  // `useQuery` can hand back a new `data` object on each render, which re-ran the effect, which set state, which
  // rendered again ("Maximum update depth exceeded").
  //
  // Only a settled answer gets held. `cache-and-network` can still be handing back the departing group's list while
  // the query for the new variables is in flight, and filing that under the new key would let a list of cases that
  // are no longer linked come back later as this group's fallback. Consumers are unaffected in the meantime:
  // `currentWorklistItems` reports just the current case while the query is loading.
  const lastLoadedGroupRef = useRef<{ queryKey: Nullable<string>; items: GroupedWorklistItems }>({
    queryKey: null,
    items: EMPTY_GROUP,
  });
  if (queryKey != null && !loadingGroup && data?.groupedWorklistItems != null) {
    lastLoadedGroupRef.current = { queryKey, items: data.groupedWorklistItems };
  }
  const heldGroup =
    lastLoadedGroupRef.current.queryKey === queryKey
      ? lastLoadedGroupRef.current.items
      : EMPTY_GROUP;
  const groupedWorklistItems = data?.groupedWorklistItems ?? heldGroup;

  const loading = loadingCase || loadingGroup;

  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useCrossTabEventsListener(NameSpaces.CROSS_WINDOW_DATA_REFETCH, ({ source, payload }) => {
    if (source === 'remote' && payload.type === CrossWindowDataRefetchType.WORKLIST_ITEM_GROUP) {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
      }

      refetchTimerRef.current = setTimeout(async () => {
        refetchTimerRef.current = null;
        await refreshCase();
        await refetch();
      }, 100);
    }
  });

  const currentWorklistItems = useMemo(() => {
    if (worklistItem == null || loading) return [];

    const currentCaseAsGroupedWorklistItem = {
      smid: worklistItem.smid,
      accessionNumber: worklistItem.accessionNumber,
      patientAge: worklistItem.patientAge,
      patientSex: worklistItem.patientSex,
      dosage: worklistItem.dosage,
      studyDescription: worklistItem.studyDescription,
      studyReason: worklistItem.studyReason,
      studyDate: worklistItem.studyDate,
      referringPhysician: worklistItem.referringPhysician,
      techNotes: worklistItem.techNotes,
      studies: worklistItem.studies.map((study) => ({
        laterality: study.laterality,
        studyDate: study.studyDate,
        smid: study.smid,
      })),
      customMergeFields: worklistItem.customMergeFields,
    } as const;

    // We return the current worklist items such that the first element is the current case,
    // and the rest are sorted by study description. This is consistent with the LinkCases UI.
    return groupedWorklistItems.length > 0
      ? [
          currentCaseAsGroupedWorklistItem,
          ...groupedWorklistItems
            .filter((item) => item.smid !== worklistItem.smid)
            .sort(sortByStudyDescAndAccession),
        ]
      : [currentCaseAsGroupedWorklistItem];
  }, [groupedWorklistItems, loading, worklistItem]);

  // This effect is responsible for invoking handleAdd/handleRemoveWorklistItem
  // as worklistItems are added/removed from groupedWorklistItems.
  useEffect(() => {
    if (loading || editor == null) return;

    // A baseline describes one case's report. Diffing the case the reader just opened against the one they left reads
    // as a wholesale unlink and rewrites the new report, so re-seed on a case change instead of diffing across it.
    const caseSmid = worklistItem?.smid ?? null;
    if (reconciledCaseSmidRef.current !== caseSmid) {
      reconciledCaseSmidRef.current = caseSmid;
      setPreviousWorklistItems(currentWorklistItems);
      return;
    }

    if (currentWorklistItems.length === previousWorklistItems.length) return;

    if (previousWorklistItems.length === 0) {
      setPreviousWorklistItems(currentWorklistItems);
      return;
    }

    const previousWorklistSmids = previousWorklistItems.map((item) => item.smid);
    const addedWorklistItems = currentWorklistItems.filter(
      (item) => !previousWorklistSmids.includes(item.smid) && item.smid !== worklistItem?.smid
    );
    if (addedWorklistItems.length > 0) {
      addedWorklistItems.forEach((item) =>
        handleAddWorklistItem(item, currentWorklistItems, editor)
      );
      setPreviousWorklistItems(currentWorklistItems);
      return;
    }

    const currentWorklistSmids = currentWorklistItems.map((item) => item.smid);
    const removedWorklistItems = previousWorklistItems.filter(
      (item) => !currentWorklistSmids.includes(item.smid) && item.smid !== worklistItem?.smid
    );
    if (removedWorklistItems.length > 0) {
      removedWorklistItems.forEach((item) =>
        handleRemoveWorklistItem(item, currentWorklistItems, editor)
      );
      setPreviousWorklistItems(currentWorklistItems);
      return;
    }
  }, [currentWorklistItems, editor, loading, previousWorklistItems, worklistItem?.smid]);

  const contextValue = useMemo(
    () => ({ loading, currentWorklistItems }),
    [loading, currentWorklistItems]
  );

  return (
    <CurrentWorklistItems.Provider value={contextValue}>{children}</CurrentWorklistItems.Provider>
  );
};

/**
 * Note that this hook returns a slightly different result than the query of a similar name.
 * This hook returns an array of all of the worklist items that the user is reporting on currently,
 * including the current case. Whereas the query will return an empty array, if the current case
 * is not grouped with any other worklist items.
 */
export const useCurrentWorklistItems = (): {
  loading: boolean;
  currentWorklistItems: GroupedWorklistItems;
} => {
  const currentWorklistItems = useContext(CurrentWorklistItems);

  if (!currentWorklistItems) {
    throw new Error('useCurrentWorklistItems must be used within a CurrentWorklistItemsProvider.');
  }

  return currentWorklistItems;
};

CurrentWorklistItemsProvider.displayName = 'CurrentWorklistItemsProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(CurrentWorklistItemsProvider);
